import { getInfoType } from "@/common/state/infotypes";
import { FindingObjectType, FindingsEntity } from "./findings.model";
import { get_value_from_map_or_default_to_key } from "@/fp";
import { ICONS } from "@/common/icons.map";
import { InfoTypeHelper } from "@/common/models";
import { SharedState } from "@/common/state";

interface SharepointInfoMap {
  creationTime: string;
  folderFullPath: string;
  folderName: string;
  folderUrl: string;
  itemId: string;
  itemUrl: string;
  lastUpdateTime: string;
  siteUrl: string;
  size: number;
}

const get_file_path = (entity: FindingsEntity) => {
  switch (entity.applicationId) {
    case "msft_sharepoint_365":
    default:
      return (entity.infoMap as SharepointInfoMap).itemUrl;
  }
};

const get_labels = (entity: FindingsEntity) => {
  const labels = entity.labelIds
    .filter((d) => {
      const label = SharedState.getInfoType(d);
      return (
        label &&
        label.id != SharedState.default_label.value?.id &&
        label.labelType == "Content_label"
      );
    })
    .map((d) => getInfoType(d))
    .sort((a, b) => b.generation - a.generation);
  if (labels.length > 0) {
    // return labels[0];
    return {
      piis: entity.labelIds
        .filter((d) => {
          const label = SharedState.getInfoType(d);
          return label && (label.code == 7 || label.code == 0);
        })
        .map((d) => getInfoType(d)),
      info_label: labels[0],
      sensitivity: labels[0].sensitivityLevel,
    };
  } else {
    return {
      piis: entity.labelIds
        .filter((d) => {
          const label = SharedState.getInfoType(d);
          return label && (label.code == 7 || label.code == 0);
        })
        .map((d) => getInfoType(d)),
      info_label: SharedState.default_label.value!,
      sensitivity: SharedState.default_label.value!.sensitivityLevel,
    };
  }
};

const to_file = (entity: FindingsEntity) => {
  console.log("entity.objectName: ", entity.objectName);

  const obj = {
    ...entity,
    id: entity.objectUid,
    //label,
    ...get_labels(entity),
    file_extension: ICONS.EXTENSION.get(
      entity.objectName.split(".").reverse()[0]
    ),
    path: get_file_path(entity),
  };
  return obj;
};

const from_bucket_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    file_name: entity.objectName,
    file_extension: ICONS.EXTENSION.get(
      entity.objectName.split(".").reverse()[0]
    ),
    ...get_labels(entity),
    bucket_name: entity.infoMap["bucketName"] ?? "?",
    folder_name: entity.infoMap["folderName"] ?? "?",
    access: entity.infoMap["access"] ?? "?",
    region: entity.infoMap["region"] ?? "?",
    created_at:
      entity.infoMap["dateCreated"] ?? entity.infoMap["fileDateCreated"] ?? "?",
  };
  return obj;
};

const from_file_share = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    file_name: entity.objectName,
    file_extension: ICONS.EXTENSION.get(
      entity.objectName.split(".").reverse()[0]
    ),
    ...get_labels(entity),
    storage_name: entity.infoMap["storageName"] ?? "?",
    file_share_name: entity.aggObjectName,
    folder_name: entity.infoMap["folderName"] ?? "?",
  };
  return obj;
};

const from_dropbox_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    file_name: entity.objectName,
    file_extension: ICONS.EXTENSION.get(
      entity.objectName.split(".").reverse()[0]
    ),
    ...get_labels(entity),
    folder_path: entity.infoMap["folderName"],
    root_folder: entity.aggObjectName,
    access_type: entity.infoMap["accessType"] ?? "?",
    size: entity.infoMap["size"] ?? "?",
    last_update_time: entity.infoMap["lastUpdateTime"] ?? "?",
  };
  return obj;
};

const from_az_storage_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    file_name: entity.objectName,
    file_extension: ICONS.EXTENSION.get(
      entity.objectName.split(".").reverse()[0]
    ),
    ...get_labels(entity),
    created_at: entity.activityDate,
    folder_name: entity.infoMap["folderName"] ?? "?",
    container_name: entity.infoMap["containerName"] ?? "?",
    storage_account: entity.infoMap["storageName"] ?? "?",
    access: entity.infoMap["access"] ?? "?",
    type: entity.infoMap["type"] ?? "?",
  };
  return obj;
};

const from_ms_sql_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    table_name: entity.objectName,
    detected_at: entity.activityDate,
    db_name: entity.aggObjectName ?? "?",
    server_name: entity.infoMap["server"] ?? "?",
  };
  return obj;
};

const from_snowflake_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    table_name: entity.objectName,
    detected_at: entity.activityDate,
    db_name: entity.aggObjectName ?? "?",
    account_name: entity.infoMap["account"] ?? "?",
  };
  return obj;
};

const to_readable_entity_type = get_value_from_map_or_default_to_key({
  Salesorder: "Sales order",
  salesorder: "Sales order",
});

const from_dynamics_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    view_name: entity.infoMap["viewName"],
    entity_type: to_readable_entity_type(entity.infoMap["activityContext"]),
    employee_email: entity.infoMap["exportedByEmail"] ?? "?",
    export_date: entity.activityDate,
    download_count: entity.infoMap["recordCount"] ?? "?",
  };
  return obj;
};

const from_confluence_item = (entity: FindingsEntity) => {
  const obj = {
    id: entity.objectUid,
    file_name: entity.objectName,
    space_name: entity.infoMap["spaceName"],
    page_title: entity.infoMap["pageTitle"],
    content_type: entity.infoMap["contentType"],
    activity_date: entity.activityDate,
    ...get_labels(entity),
  };
  return obj;
};

const from_sharepoint_website = (entity: FindingsEntity) => {
  const owners = (entity.infoMap["owners"] || "")
    .split(",")
    .filter((d: string) => d.length > 0);
  const obj = {
    id: entity.objectUid,
    site: entity.objectName,
    type: entity.infoMap["siteType"],
    name: entity.objectName.split("/").pop(),
    owners,
    owners_list:
      owners.length > 0
        ? owners.reduce((acc: string, email: string) => {
            acc += `\n${email}`;
            return acc;
          }, "")
        : "",
  };
  return obj;
};

export const FindingEntityMapper = {
  to_file,
  by_object_type: (type: FindingObjectType) => {
    switch (type) {
      case "Dropbox":
        return from_dropbox_item;
      case "FileShare":
        return from_file_share;
      case "Bucket":
        return from_bucket_item;
      case "Snowflake":
        return from_snowflake_item;
      case "AzStorage":
        return from_az_storage_item;
      case "MsSql":
        return from_ms_sql_item;
      case "Dynamics":
        return from_dynamics_item;
      case "SharepointWebsite":
        return from_sharepoint_website;
      case "Confluence":
        return from_confluence_item;
      case "File":
      default:
        return to_file;
    }
  },
};
